import { IBaseShippbillAddress } from "core/types/entities";
import { ICustomReactSelectOption } from "core/types/elements/ICustomReactSelectOption";
import { IVendor } from "core/types/entities/IVendor";
import moment from "moment";
import ExpensesTypes from "../types/enums/ExpensesEnum";
import { IUser } from "../types/base/IUser";
import { ITimeZones } from "../types/entities/IItem";

const convertShippbillAddress = (data: IBaseShippbillAddress, isFreeForm: boolean) => {
  const addressArr = isFreeForm
    ? [data?.line1, data?.line2, data?.line3, data?.line4, data?.line5]
    : [data?.city, data?.country, data?.countrySubDivisionCode, data?.postalCode];
  return addressArr.filter(item => item).join(", ");
};

const convertCurrency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const numberToLocaleStr = (inputNumber: number) => inputNumber.toLocaleString("en-US");

const converTimeToHour = (value: string, text?: string) => {
  const times = value.split(":");
  return `${Number(times[0])}${Number(times[1]) ? `.${Number(times[1])}` : ""} ${text || "hour"}`;
};

const convertTechServerStatusToUI = (status: string | undefined) => {
  if (!status) return null;
  switch (status) {
    case "WorkDone":
      return "Work Done";
    case "CustomerContacted":
      return "Customer Contacted";
    case "InRoute":
      return "In Route";
    case "OnSite":
      return "On Site";
    case "InProcess":
      return "In Process";
    default:
      return status;
  }
};

const convetStatusesToSelectOptions = <T extends Object>(
  value: T,
  data?: any,
): ICustomReactSelectOption[] => {
  return Object.values(value)
    .filter(item => typeof item === "string")
    .map((item, index) => ({
      value: item,
      label: convertTechServerStatusToUI(item) || "",
      selectDisabled: data ? data[index] : undefined,
      selectData: "limitedHeight",
    }));
};

const convertUsersToSelectOptions = <T extends IUser[]>(value: T): ICustomReactSelectOption[] => {
  return value.map(item => ({
    value: `${item.firstName} ${item.lastName}`,
    label: `${item.firstName} ${item.lastName}`,
    extraData: item,
    selectData: "limitedHeight",
  }));
};

const convertTimezonesToSelectOptions = <T extends ITimeZones[]>(
  value: T,
): ICustomReactSelectOption[] => {
  return value.map(item => ({
    value: `${item.displayName}`,
    label: `${item.displayName}`,
    extraData: item,
    selectData: "limitedHeight",
  }));
};

const convertTimezoneToSelectOptions = <T extends ITimeZones[]>(
  value: T,
): ICustomReactSelectOption[] => {
  return value.map(item => ({
    value: item.id,
    label: item.displayName,
    extraData: item,
    selectData: "limitedHeight",
  }));
};

const convertVendorToSelectOptions = <T extends IVendor[]>(
  value: T,
): ICustomReactSelectOption[] => {
  return value.map(item => ({
    value: `${item.id}`,
    label: `${item.name}`,
    extraData: item,
    selectData: "limitedHeight",
  }));
};

const convertNumberToTime = (value: number): string => {
  const numberLessTen = 9;
  const estimateFormat = String(value).split(".");
  let estimateDate =
    Number(estimateFormat[0]) > numberLessTen
      ? `${String(estimateFormat[0])}:`
      : `0${String(estimateFormat[0])}:`;
  if (estimateFormat.length > 1) {
    estimateDate +=
      Number(estimateFormat[1]) > numberLessTen
        ? `${String(estimateFormat[1])}:00`
        : `0${String(estimateFormat[1])}:00`;
  } else {
    estimateDate += "00:00";
  }
  return estimateDate;
};

const convertStingTimeToHoursWithMinutes = (duration: string) => {
  return moment(duration, "HH:mm:ss").format("hh:mm");
};

const converStringTimeToDateTime = (duration: string): Date => {
  return moment(duration, "HH:mm:ss").toDate();
};

const convertSymbolToDouble = (value: string | number): string => {
  if (String(value).length === 1) {
    return `0${value}`;
  }
  return String(value);
};

const convertDurationTime = (timeStmp: string) => {
  const time = timeStmp.split(".");
  return time.length ? time[0] : "00:00:00";
};

const convertDurationToHourString = (timeStmp: string) => {
  const time = timeStmp.split(":");
  const splidDay = time[0].split(".");
  return time.length
    ? `${
        splidDay.length > 1
          ? `${Number(splidDay[0])}d ${Number(splidDay[1])}h`
          : `${Number(splidDay[0])}h`
      } ${Number(time[1])}m`
    : "0h 0m";
};

const convertExpenseTypeName = (recordType: string): string => {
  if (recordType === ExpensesTypes.MiscItem) {
    return "Miscellaneous";
  }
  if (recordType === ExpensesTypes.TravelOT) {
    return "Travel Overtime";
  }
  if (recordType === ExpensesTypes.LaborOT) {
    return "Labor Overtime";
  }
  return recordType;
};

const definitionDifferenceTwoDate = (startTime: Date, endTime: Date) => {
  const diff = endTime.getTime() - startTime.getTime();
  const SEC = 1000;
  const MIN = 60 * SEC;
  const HRS = 60 * MIN;

  const hrs = Math.floor(diff / HRS);
  const min = Math.floor((diff % HRS) / MIN);
  const sec = Math.floor((diff % MIN) / SEC);
  const stopwatchOffset = new Date();
  stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + sec);
  stopwatchOffset.setMinutes(stopwatchOffset.getMinutes() + min);
  stopwatchOffset.setHours(stopwatchOffset.getHours() + hrs);
  return stopwatchOffset;
};

const convertDateToStringTime = (date: Date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const numberLessTen = 9;

  return `${hours > numberLessTen ? `${hours}:` : `0${hours}:`}${
    minutes > numberLessTen ? `${minutes}` : `0${minutes}`
  }`;
};

const mappingEventColors = () => {
  const hue = Math.floor(Math.random() * 360);
  return `hsl(${hue}, 100%, 80%)`;
};

const sortTimezones = (data: ITimeZones[]) => {
  const compareTimezones = (a: any, b: any) => {
    const regex = /\(UTC([+-]\d{1,2}):/;
    // eslint-disable-next-line no-sparse-arrays
    const [, aOffset] = a.displayName.match(regex) || [, "0"];
    // eslint-disable-next-line no-sparse-arrays
    const [, bOffset] = b.displayName.match(regex) || [, "0"];
    return parseInt(aOffset, 10) - parseInt(bOffset, 10);
  };

  const sortedTimezones = [...data].sort(compareTimezones);

  return sortedTimezones;
};

const calculateDuration = (startTime: string | Date, endTime: string | Date): string => {
  const start = moment(startTime);
  const end = moment(endTime);

  if (!end.isAfter(start)) {
    throw new Error("End time must be after start time");
  }

  const duration = moment.duration(end.diff(start));

  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  return `${hours}h ${minutes}m`;
};

const calculateTotalDuration = (
  ranges: { startTime: string | Date; endTime: string | Date }[],
): string => {
  const totalDuration = moment.duration(0);

  ranges.forEach((range, index) => {
    const { startTime, endTime } = range;

    try {
      const start = moment(startTime);
      const end = moment(endTime);

      if (!end.isAfter(start)) {
        throw new Error(`End time must be after start time for range at index ${index}`);
      }

      const duration = moment.duration(end.diff(start));
      totalDuration.add(duration);
    } catch (error) {
      console.error(`Error calculating duration for range at index ${index}:`, error);
      // Depending on requirements, you might want to handle the error differently
    }
  });

  const hours = Math.floor(totalDuration.asHours());
  const minutes = totalDuration.minutes();

  return `${hours}h ${minutes}m`;
};

export {
  convertTimezonesToSelectOptions,
  sortTimezones,
  convertTimezoneToSelectOptions,
  convertShippbillAddress,
  convertCurrency,
  numberToLocaleStr,
  converTimeToHour,
  convetStatusesToSelectOptions,
  convertNumberToTime,
  convertStingTimeToHoursWithMinutes,
  converStringTimeToDateTime,
  convertTechServerStatusToUI,
  convertSymbolToDouble,
  convertDurationTime,
  convertExpenseTypeName,
  convertDurationToHourString,
  definitionDifferenceTwoDate,
  convertDateToStringTime,
  mappingEventColors,
  convertUsersToSelectOptions,
  calculateDuration,
  calculateTotalDuration,
  convertVendorToSelectOptions,
};
